/*
 * Adds ability to render custom dropdown that accepts any HTML you provide.
 * It will make sure dropdown is positioned correctly and toggle it for you.
 *
 * Requirements:
 *
 *  - You need to provide the data (`data-custom-dropdown-data-value`) that will
 *    be used to render proper label and icon (optional) based on selected value.
 *  - Inside dropdown you need to have element(s) with `data-action="custom-dropdown#select"`
 *    and `data-custom-dropdown-value-param="yourValue"` data attributes
 *
 * Usage example:
 *
 * <div
 *  data-controller="custom-dropdown"
 *  data-custom-dropdown-target="wrapper"
 *  data-custom-dropdown-data-value="{'hr':{'label':'Croatia','icon':'flags/hr.svg'},'de':{'label':'Germany','icon':'flags/de.svg'}"
 *  data-custom-dropdown-icon-right-value="select_chevron.svg"
 *  class="custom-dropdown-wrapper">
 *  <input
 *    name="country_display"
 *    class="custom-dropdown-input"
 *    value="Croatia"
 *    data-custom-dropdown-target="input"
 *    data-action="focus->custom-dropdown#show click->custom-dropdown#show" type="text">
 *  <input name="country" data-custom-dropdown-target="hiddenInput" autocomplete="off" type="hidden" value="hr">
 *  <div class="custom-dropdown-dropdown" data-custom-dropdown-target="dropdown">
 *    <button type="button" data-action="custom-dropdown#select" data-custom-dropdown-value-param="hr"><img title="Croatia" src="flags/hr.svg"></button>
 *    <button type="button" data-action="custom-dropdown#select" data-custom-dropdown-value-param="de"><img title="Germany" src="flags/de.svg"></button>
 *  </div>
 * </div>
 */
import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = [
    "dropdown",
    "hiddenInput",
    "input",
    "wrapper"
  ]

  static values = {
    data: Object,
    iconRight: String
  }

  connect() {
    this.displayIcons()
    useClickOutside(this, { element: this.dropdownTarget })

    this.dispatch("connected")
  }

  displayIcons() {
    let backgroundImage = `url(${this.iconRightValue})`
    let backgroundPosition = "center right 10px"
    if (this.iconLeft) {
      backgroundImage += `, url(${this.iconLeft})`
      backgroundPosition += ", center left 5px"
      this.inputTarget.classList.add("pl-7")
    }
    this.inputTarget.style.backgroundImage = backgroundImage
    this.inputTarget.style.backgroundPosition = backgroundPosition
  }

  clickOutside(event) {
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.hide()
    }
  }

  show() {
    let viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const wrapperRect = this.wrapperTarget.getBoundingClientRect()
    const inputRect = this.inputTarget.getBoundingClientRect()
    const stickyFooter = document.getElementsByClassName("submit-buttons-sticky")[0]

    if (stickyFooter) {
      // Reduce sticky footer height from viewport
      viewportHeight -= stickyFooter.getBoundingClientRect().height
      // Set z-index same sa sticky submit bar in footer so it goes above everything except it
      this.wrapperTarget.style.zIndex = window.getComputedStyle(stickyFooter).zIndex
    }

    // Remove current top value before we start calulating
    this.dropdownTarget.style.removeProperty("top")
    this.dropdownTarget.classList.remove("hidden")

    const wrapperTopY = wrapperRect.y
    const inputBottomY = inputRect.y + inputRect.height
    this.dropdownTarget.style.top = `${inputBottomY - wrapperTopY}px`

    // Get current rect and make sure it is moved above input if it will not be fully visible below input
    const dropdownRect = this.dropdownTarget.getBoundingClientRect()
    if ((dropdownRect.y + dropdownRect.height) > (viewportHeight)) {
      this.dropdownTarget.style.top = `-${dropdownRect.height - (inputRect.y - wrapperTopY)}px`
    }
  }

  hide() {
    this.dropdownTarget.classList.add("hidden")
    this.wrapperTarget.style.removeProperty("z-index")
  }

  select(event) {
    const value = event.params.value
    this.setValue(value)
    this.dispatch("selected", { detail: { ...this.dataValue[value], ...{ value: value } } })
  }

  setValue(value) {
    this.hiddenInputTarget.value = value
    this.setLabel(this.dataValue[value]["label"])
    this.displayIcons()
    this.hide()
  }

  setLabel(label) {
    this.inputTarget.value = label
  }

  getValue() {
    return this.hiddenInputTarget.value
  }

  getLabel() {
    return this.inputTarget.value
  }

  get iconLeft() {
    const data = this.dataValue[this.getValue()]

    return data ? data["icon"] : null
  }
}
